import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, EmailValidator, Validators } from '@angular/forms';
import { Invoice, InvoiceCategory } from '../../model/invoice';
import { InvoiceService } from '../../service/data/invoice.service';
import { IncorrectPaymentService } from '../../service/data/incorrect-payment.service';
import { PaymentService } from '../../service/data/payment.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { IncorrectPayment } from '../../model/incorrect-payment';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { Observable } from 'rxjs';
import { MoneyValidator } from '../../../core/service/money-validator';
import { Money } from '../../../core/model/money';
import { SelectionItem } from '../../../core/component/select/select.component';
import { UnpaidInvoiceSelection } from '../../model/unpaid-invoice-selection';
import { areDifferentCompanies } from '../../helper/company-comparison';

@Component({
  selector: 'app-pay-invoice-form',
  templateUrl: './pay-invoice-form.component.html',
  styleUrls: ['./pay-invoice-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PayInvoiceFormComponent implements OnInit, IAutoCompleteService {
  constructor(
    private _actionService: ActionWindowService,
    private _invoiceService: InvoiceService,
    private _paymentService: IncorrectPaymentService,
    private _manualPaymentService: PaymentService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    this.form = fb.group({
      amount: fcf.createMandatory('INCORRECT_PAYMENT.AMOUNT', 0),
      businessTransactionDate: fb.group({}),
      invoice: fcf.createMandatory('INCORRECT_PAYMENT.INVOICE', null),
      includeAllCompanies: fcf.create('INCORRECT_PAYMENT.INCLUDE_ALL_COMPANIES', false),
      companyBankAccountId: fcf.create('REGISTER_PAYMENT.PAYMENT_METHOD', ''),
    });
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem> {
    return this._invoiceService.getSelectionItem(id);
  }
  getSelection(
    filter: string,
    options: { onlyCredit: boolean; companyId?: string | null },
    page?: number
  ): Observable<AutoCompleteSelectionItem<UnpaidInvoiceSelection>[]> {
    return this._invoiceService.getUnpaidSelection(filter, options, page);
  }

  @Input() dataContext: IncorrectPayment;

  form: FormGroup;
  differentCompany = false;
  informationExpanded = false;
  invoiceSearchOptions: { onlyCredit: boolean; companyId?: string | null };
  selectedInvoice: UnpaidInvoiceSelection;
  accountSelection: SelectionItem[] = [];
  cancel() {
    this._actionService.close();
  }

  get filterService(): IAutoCompleteService {
    return this;
  }

  toggleInformation() {
    this.informationExpanded = !this.informationExpanded;
  }

  invoiceSelected(invoice: AutoCompleteSelectionItem<UnpaidInvoiceSelection>) {
    this.selectedInvoice = invoice?.data;
    this.accountSelection = [];
    this.form.controls['companyBankAccountId'].setValue('');
    this.form.controls['companyBankAccountId'].clearValidators();

    if (!invoice) {
      this.differentCompany = false;
      this.form.controls['companyBankAccountId'].updateValueAndValidity();
      return;
    }

    let amount = this.dataContext.balance;
    this.differentCompany = areDifferentCompanies(invoice.data, this.dataContext);

    if (!this.differentCompany && amount.IsPositive && invoice.data.remainingAmount.amount < this.dataContext.balance.amount)
      amount = invoice.data.remainingAmount;

    if (!this.differentCompany && amount.IsNegative && invoice.data.remainingAmount.amount > this.dataContext.balance.amount)
      amount = invoice.data.remainingAmount;

    this.form.controls['amount'].setValue(amount);

    if (this.differentCompany) {
      this.form.controls['companyBankAccountId'].setValidators(Validators.required);
      this._manualPaymentService.getRegisterPaymentViewModel(invoice.value).subscribe(model => {
        if (!this.selectedInvoice || this.selectedInvoice.id !== invoice.value) {
          return;
        }

        this.accountSelection = model.companyBankAccounts.map(account => new SelectionItem(account.name, account.id));
        const defaultAccount = model.companyBankAccounts.find(account => account.defaultInvoiceAccount) || model.companyBankAccounts[0];
        if (defaultAccount) {
          this.form.controls['companyBankAccountId'].setValue(defaultAccount.id);
        }
      });
    }

    this.form.controls['companyBankAccountId'].updateValueAndValidity();
  }

  get surplusAmount(): Money {
    if (!this.selectedInvoice || !this.differentCompany) {
      return null;
    }

    const amount = this.form.controls['amount'].value;
    if (!amount || amount.amount <= this.selectedInvoice.remainingAmount.amount) {
      return null;
    }

    return new Money(amount.currencyCode, amount.amount - this.selectedInvoice.remainingAmount.amount);
  }

  ngOnInit() {
    this.dataContext.balance = new Money(this.dataContext.balance.currencyCode, this.dataContext.balance.amount);
    this.form.controls['amount'].setValue(this.dataContext.balance);
    if (this.dataContext.balance.IsNegative)
      this.form.controls['amount'].setValidators(
        Validators.compose([
          Validators.required,
          MoneyValidator.moneyBetween(this.dataContext.balance, new Money(this.dataContext.balance.currencyCode, -0.01)),
        ])
      );
    else
      this.form.controls['amount'].setValidators(
        Validators.compose([
          Validators.required,
          MoneyValidator.moneyBetween(new Money(this.dataContext.balance.currencyCode, 0.01), this.dataContext.balance),
        ])
      );

    this.form.controls['businessTransactionDate'].setValue(this.dataContext.businessTransactionDate.toDate());
    this.invoiceSearchOptions = {
      onlyCredit: this.dataContext.balance.IsNegative,
      companyId: this.dataContext.companyId,
    };
    this.form.controls['includeAllCompanies'].valueChanges.subscribe(includeAllCompanies => {
      this.invoiceSearchOptions = {
        onlyCredit: this.dataContext.balance.IsNegative,
        companyId: includeAllCompanies ? null : this.dataContext.companyId,
      };
    });
  }

  working: boolean;

  onSubmit() {
    if (this.form.invalid || this.working) {
      return;
    }

    this.working = true;

    this._paymentService
      .payInvoice(
        this.form.value.amount,
        this.dataContext.id,
        this.form.value.invoice,
        this.form.value.businessTransactionDate,
        this.differentCompany ? this.form.value.companyBankAccountId : null
      )
      .subscribe(payment => {
        this._actionService.complete(payment);
        this.working = false;
        this._actionService.close();
      });
  }
}
